import { Route } from '@angular/router';
import { authGuard, guestGuard, tfaAuthGuard } from '@trading-fe/guards';
import { companyUserIdResolver } from './pages/company/company-context';

export const appRoutes: Route[] = [
  {
    path: '',
    loadComponent: () => import('./pages/login/login').then((m) => m.Login),
    canActivate: [guestGuard],
  },
  {
    path: 'tfa',
    loadComponent: () => import('./pages/tfa/tfa').then((m) => m.Tfa),
    canActivate: [tfaAuthGuard],
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password').then(
        (m) => m.ForgotPassword
      ),
    canActivate: [guestGuard],
  },
  {
    path: 'admin',
    loadComponent: () =>
      import('./components/layout/layout').then((m) => m.Layout),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'users',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard/dashboard').then((m) => m.Dashboard),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./pages/users/users').then((m) => m.Users),
      },
      {
        path: 'companies',
        loadComponent: () =>
          import('./pages/companies/companies').then((m) => m.Companies),
      },
      // A corporate account. The money tabs are the user tabs: the resolver puts the company's
      // invited user in route data as `userId`, which they read through accountUserParams().
      {
        path: 'company/:id',
        loadComponent: () =>
          import('./pages/company/company').then((m) => m.CompanyPage),
        resolve: { userId: companyUserIdResolver },
        // Re-resolve on a same-URL reload, so inviting the user updates the money tabs.
        runGuardsAndResolvers: 'always',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/company/company-info/company-info').then(
                (m) => m.CompanyInfo
              ),
          },
          {
            path: 'kyc',
            loadComponent: () =>
              import('./pages/company/company-kyc/company-kyc').then(
                (m) => m.CompanyKyc
              ),
          },
          {
            path: 'questionnaire',
            loadComponent: () =>
              import('./pages/company/company-questionnaire/company-questionnaire').then(
                (m) => m.CompanyQuestionnaireTab
              ),
          },
          {
            path: 'documents',
            loadComponent: () =>
              import('./pages/company/company-documents/company-documents').then(
                (m) => m.CompanyDocuments
              ),
          },
          {
            path: 'users',
            loadComponent: () =>
              import('./pages/company/company-users/company-users').then(
                (m) => m.CompanyUsers
              ),
          },
          {
            path: 'dashboard',
            data: { requiresUser: true },
            loadComponent: () =>
              import('./pages/user/user-dashboard/user-dashboard').then((m) => m.UserDashboard),
          },
          {
            path: 'wallets',
            data: { requiresUser: true },
            loadComponent: () =>
              import('./pages/user/user-wallets/user-wallets').then((m) => m.UserWallets),
          },
          {
            path: 'open-orders',
            data: { requiresUser: true },
            loadComponent: () =>
              import('./pages/user/user-open-orders/user-open-orders').then((m) => m.UserOpenOrders),
          },
          {
            path: 'order-history',
            data: { requiresUser: true },
            loadComponent: () =>
              import('./pages/user/user-order-history/user-order-history').then((m) => m.UserOrderHistory),
          },
          {
            path: 'otc-order-history',
            data: { requiresUser: true },
            loadComponent: () =>
              import('./pages/user/user-otc-order-history/user-otc-order-history').then((m) => m.UserOtcOrderHistory),
          },
          {
            path: 'trades',
            data: { requiresUser: true },
            loadComponent: () =>
              import('./pages/user/user-trades/user-trades').then((m) => m.UserTrades),
          },
          {
            path: 'transactions',
            data: { requiresUser: true },
            loadComponent: () =>
              import('./pages/user/user-transactions/user-transactions').then((m) => m.UserTransactions),
          },
          {
            path: 'loans',
            data: { requiresUser: true },
            loadComponent: () =>
              import('./pages/user/user-loans/user-loans').then((m) => m.UserLoans),
          },
        ],
      },
      {
        path: 'user/:id',
        loadComponent: () => import('./pages/user/user').then((m) => m.User),
        children: [
          {
            path: 'dashboard',
            loadComponent: () =>
              import('./pages/user/user-dashboard/user-dashboard').then(
                (m) => m.UserDashboard
              ),
          },
          {
            path: '',
            loadComponent: () =>
              import('./pages/user/user-info/user-info').then(
                (m) => m.UserInfo
              ),
          },
          {
            path: 'settings',
            loadComponent: () =>
              import('./pages/user/user-settings/user-settings').then(
                (m) => m.UserSettings
              ),
          },
          {
            path: 'open-orders',
            loadComponent: () =>
              import('./pages/user/user-open-orders/user-open-orders').then(
                (m) => m.UserOpenOrders
              ),
          },
          {
            path: 'trades',
            loadComponent: () =>
              import('./pages/user/user-trades/user-trades').then(
                (m) => m.UserTrades
              ),
          },
          {
            path: 'gtn-orders',
            loadComponent: () =>
              import('./pages/user/user-gtn-orders/user-gtn-orders').then(
                (m) => m.UserGtnOrders
              ),
          },
          {
            path: 'gtn-open-orders',
            loadComponent: () =>
              import(
                './pages/user/user-gtn-open-orders/user-gtn-open-orders'
              ).then((m) => m.UserGtnOpenOrders),
          },
          {
            path: 'order-history',
            loadComponent: () =>
              import('./pages/user/user-order-history/user-order-history').then(
                (m) => m.UserOrderHistory
              ),
          },
          {
            path: 'otc-order-history',
            loadComponent: () =>
              import(
                './pages/user/user-otc-order-history/user-otc-order-history'
              ).then((m) => m.UserOtcOrderHistory),
          },
          {
            path: 'wallets',
            loadComponent: () =>
              import('./pages/user/user-wallets/user-wallets').then(
                (m) => m.UserWallets
              ),
          },
          {
            path: 'loans',
            loadComponent: () =>
              import('./pages/user/user-loans/user-loans').then(
                (m) => m.UserLoans
              ),
          },
          {
            path: 'referrals',
            loadComponent: () =>
              import('./pages/user/user-referrals/user-referrals').then(
                (m) => m.UserReferrals
              ),
          },
          {
            path: 'transactions',
            loadComponent: () =>
              import('./pages/user/user-transactions/user-transactions').then(
                (m) => m.UserTransactions
              ),
          },
          {
            path: 'summary',
            loadComponent: () =>
              import('./pages/user/user-summary/user-summary').then(
                (m) => m.UserSummary
              ),
          },
          {
            path: 'questionary',
            loadComponent: () =>
              import('./pages/user/user-questionary/user-questionary').then(
                (m) => m.UserQuestionary
              ),
          },
          {
            path: 'documents',
            loadComponent: () =>
              import('./pages/user/user-documents/user-documents').then(
                (m) => m.UserDocuments
              ),
          },
          {
            path: 'ekeng',
            loadComponent: () =>
              import('./pages/user/user-ekeng/user-ekeng').then(
                (m) => m.UserEkeng
              ),
          },
          {
            path: 'fees',
            loadComponent: () =>
              import('./pages/settings/fee-management/fee-management').then(
                (m) => m.FeeManagement
              ),
          },
          {
            path: 'sales-clients',
            loadComponent: () =>
              import('./pages/user/user-sales-clients/user-sales-clients').then(
                (m) => m.UserSalesClients
              ),
          },
        ],
      },
      {
        path: 'trades',
        loadComponent: () =>
          import('./pages/trades/trades').then((m) => m.Trades),
      },
      {
        path: 'order-history',
        loadComponent: () =>
          import('./pages/order-history/order-history').then(
            (m) => m.OrderHistory
          ),
      },
      {
        path: 'otc-order-history',
        loadComponent: () =>
          import('./pages/otc-order-history/otc-order-history').then(
            (m) => m.OtcOrderHistory
          ),
      },
      {
        path: 'open-orders',
        loadComponent: () =>
          import('./pages/open-orders/open-orders').then((m) => m.OpenOrders),
      },
      {
        path: 'payouts',
        loadComponent: () =>
          import('./pages/payouts/payouts').then((m) => m.Payouts),
      },
      {
        path: 'transactions',
        loadComponent: () =>
          import('./pages/transactions/transactions').then(
            (m) => m.Transactions
          ),
      },
      {
        path: 'requests',
        loadComponent: () =>
          import('./pages/requests/requests').then((m) => m.Requests),
      },
      {
        path: 'loans',
        loadComponent: () => import('./pages/loans/loans').then((m) => m.Loans),
      },
      {
        path: 'tools',
        loadComponent: () =>
          import('./pages/tools/tools').then((m) => m.Tools),
        children: [
          { path: '', redirectTo: 'corporate-actions', pathMatch: 'full' },
          {
            path: 'corporate-actions',
            loadComponent: () =>
              import('./pages/tools/corporate-actions/corporate-actions').then(
                (m) => m.CorporateActions
              ),
          },
          {
            path: 'instrument-migration',
            loadComponent: () =>
              import(
                './pages/tools/instrument-migration/instrument-migration'
              ).then((m) => m.InstrumentMigration),
          },
          {
            path: 'bond-maturity',
            loadComponent: () =>
              import('./pages/tools/bond-maturity/bond-maturity').then(
                (m) => m.BondMaturity
              ),
          },
          {
            path: 'custody-fee-import',
            loadComponent: () =>
              import(
                './pages/tools/custody-fee-import/custody-fee-import'
              ).then((m) => m.CustodyFeeImport),
          },
          {
            path: 'external-orders',
            loadComponent: () =>
              import(
                './pages/tools/external-orders/external-orders'
              ).then((m) => m.ExternalOrders),
          },
          {
            path: 'push-notifications',
            loadComponent: () =>
              import(
                './pages/tools/push-notifications/push-notifications'
              ).then((m) => m.PushNotifications),
          },
        ],
      },
      // Both tools moved under /admin/tools. Keep the old URLs working —
      // they are bookmarked, and the conversions one was three levels deep.
      {
        path: 'corporate-actions',
        redirectTo: 'tools/corporate-actions',
        pathMatch: 'full',
      },
      {
        path: 'settings/instruments/conversions',
        redirectTo: 'tools/instrument-migration',
        pathMatch: 'full',
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings/settings').then((m) => m.Settings),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/settings/currencies/currencies').then(
                (m) => m.Currencies
              ),
          },
          {
            path: 'pairs',
            loadComponent: () =>
              import('./pages/settings/pairs/pairs').then((m) => m.Pairs),
          },
          {
            path: 'fx-rates',
            loadComponent: () =>
              import('./pages/settings/fx-rates/fx-rates').then(
                (m) => m.FxRates
              ),
          },
          {
            path: 'instrument-rates',
            loadComponent: () =>
              import(
                './pages/settings/instrument-rates/instrument-rates'
              ).then((m) => m.InstrumentRates),
          },
          {
            path: 'instruments',
            loadComponent: () =>
              import(
                './pages/settings/instruments/instruments-hub/instruments-hub'
              ).then((m) => m.InstrumentsHub),
            children: [
              { path: '', redirectTo: 'list', pathMatch: 'full' },
              {
                path: 'list',
                loadComponent: () =>
                  import('./pages/settings/instruments/instruments').then(
                    (m) => m.Instruments
                  ),
              },
              {
                path: 'otc',
                loadComponent: () =>
                  import(
                    './pages/settings/otc-instruments/otc-instruments'
                  ).then((m) => m.OtcInstruments),
              },
              {
                path: 'otc-sources',
                loadComponent: () =>
                  import('./pages/settings/otc-sources/otc-sources').then(
                    (m) => m.OtcSources
                  ),
              },
            ],
          },
          {
            path: 'groups',
            loadComponent: () =>
              import('./pages/settings/user-groups/user-groups').then(
                (m) => m.UserGroups
              ),
          },
          {
            path: 'maintenance',
            loadComponent: () =>
              import('./pages/settings/maintenance/maintenance').then(
                (m) => m.Maintenance
              ),
          },
          {
            path: 'advanced-settings',
            loadComponent: () =>
              import(
                './pages/settings/advanced-settings/advanced-settings'
              ).then((m) => m.AdvancedSettings),
          },
          {
            path: 'banks',
            loadComponent: () =>
              import('./pages/settings/banks/banks').then((m) => m.Banks),
          },
          {
            path: 'custodians',
            loadComponent: () =>
              import('./pages/settings/custodians/custodians').then(
                (m) => m.Custodians
              ),
          },
          {
            path: 'fees',
            loadComponent: () =>
              import('./pages/settings/fee-management/fee-management').then(
                (m) => m.FeeManagement
              ),
          },
          {
            path: 'team',
            loadComponent: () =>
              import('./pages/settings/team/team').then((m) => m.Team),
          },
        ],
      },
      {
        path: 'sync-logs',
        loadComponent: () =>
          import('./pages/sync-logs/sync-logs').then((m) => m.SyncLogs),
        children: [
          { path: '', redirectTo: '1c', pathMatch: 'full' },
          {
            path: '1c',
            loadComponent: () =>
              import('./pages/sync-logs/one-c-sync-logs/one-c-sync-logs').then(
                (m) => m.OneCyncLogs
              ),
          },
          {
            path: 'fx',
            loadComponent: () =>
              import('./pages/sync-logs/fx-sync-logs/fx-sync-logs').then(
                (m) => m.FxSyncLogs
              ),
          },
          {
            path: 'ekeng',
            loadComponent: () =>
              import('./pages/sync-logs/ekeng-logs/ekeng-logs').then(
                (m) => m.EkengLogs
              ),
          },
        ],
      },
      {
        path: 'ekeng',
        loadComponent: () =>
          import('./pages/ekeng/ekeng').then((m) => m.Ekeng),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile/profile').then((m) => m.Profile),
      },
    ],
  },
  {
    path: '**',
    loadComponent: () =>
      import('./pages/not-found/not-found').then((m) => m.NotFound),
  },
];
